import React, { useState, useRef, useEffect } from "react";
import { Upload, Download } from 'lucide-react';
import { Toast } from "primereact/toast";
import API_BASE_URL from "../../Config";
import { ClimbingBoxLoader } from "react-spinners";

const ServiceCertificate = () => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [entityList, setEntityList] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState("");
  const [loading, setLoading] = useState(false);
  const toast = useRef(null);
  const fileInputRef = useRef(null);

  // Extract user data
  const roleId = parseInt(sessionStorage.getItem("user_role_id"), 10);
  const clientId = sessionStorage.getItem("client_id");
  const username = sessionStorage.getItem("current_username")
  const companyName = sessionStorage.getItem("company_name")
  const token = sessionStorage.getItem("token");

  const isUploader = roleId === 2 || roleId === 3;

  // Fetch entities if superadmin
  useEffect(() => {
    if (roleId === 1) {
      fetch(`${API_BASE_URL}api/overall_clients/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => res.json())
        .then((data) => {
          console.log("Client API response:", data);
          if (data.data && Array.isArray(data.data.client)) {
            setEntityList(data.data.client);
          } else {
            setEntityList([]);
          }
        })
        .catch(() => {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: "Failed to load clients.",
            life: 0, // Never auto-hide
            closable: true,
            sticky: true,
          });
        });
    }
  }, [roleId, token]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && /\.(xlsx)$/i.test(file.name)) {
      setUploadedFile(file);
    } else {
      toast.current?.show({
        severity: "warn",
        summary: "Invalid File",
        detail: "Only .xlsx files are allowed.",
        life: 0, // Never auto-hide
        closable: true,
        sticky: true,
      });
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDownload = async () => {
    const token = sessionStorage.getItem("token");
    const roleId = sessionStorage.getItem("user_role_id");
    const username = sessionStorage.getItem("current_username");
    const companyName =
      roleId === "1" ? selectedEntity : sessionStorage.getItem("company_name");

    if (!companyName) {
      toast.current?.show({
        severity: "error",
        summary: "Company Missing",
        detail: "Please select or log in with a valid company.",
        life: 0, // Never auto-hide
        closable: true,
        sticky: true,
      });
      return;
    }

    if (roleId === "3" && !username) {
      toast.current?.show({
        severity: "error",
        summary: "Missing Username",
        detail: "Username is required for role 3 to download.",
        life: 0, // Never auto-hide
        closable: true,
        sticky: true,
      });
      return;
    }

    setLoading(true);

    try {
      let query = `company_name=${encodeURIComponent(companyName)}&role_id=${roleId}`;
      if (roleId === "3") {
        query += `&username=${encodeURIComponent(username)}`;
      }

      const res = await fetch(`${API_BASE_URL}api/download_service_letter_template/?${query}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        toast.current?.show({
          severity: "warn",
          summary: "Download Failed",
          detail:
            res.status === 404
              ? "Service Certificate not available for this company."
              : "Failed to download file.",
          life: 0, // Never auto-hide
          closable: true,
          sticky: true,
        });
        return;
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
       // Extract filename from Content-Disposition header

      let filename = "ServiceCertificate.xlsx" // Default fallback

      const contentDisposition = res.headers.get("Content-Disposition")

      if (contentDisposition) {

        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)

        if (filenameMatch && filenameMatch[1]) {

          filename = filenameMatch[1].replace(/['"]/g, "")

        }

      }

      link.setAttribute("download", filename) // Changed this line
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.current?.show({
        severity: "success",
        summary: "Download Complete",
        detail: "Service Certificate downloaded successfully!",
        life: 6000,
        closable: true,
        
      });
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Download Error",
        detail: error.message,
        life: 0, // Never auto-hide
        closable: true,
        sticky: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!uploadedFile) {
      toast.current?.show({
        severity: "warn",
        summary: "Upload Required",
        detail: "Please upload a file before submitting.",
        life: 0, // Never auto-hide
        closable: true,
        sticky: true,
      });
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("excel_file", uploadedFile);
    formData.append("client_id", clientId);
    formData.append("username", username || "");
    formData.append("company_name", companyName || "");
    formData.append("role_id", roleId);
    if (roleId === 1) formData.append("entity_id", selectedEntity);

    try {
  const response = await fetch(`${API_BASE_URL}api/service_letter_creation/`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  const contentDisposition = response.headers.get("Content-Disposition");
  const isFileResponse = contentDisposition?.includes("attachment");

  // CASE 1: Backend returns a file (could be success OR error file)
  if (isFileResponse) {
    const blob = await response.blob();
    
    // Extract filename
    let filename = "processed_file.xlsx";
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match?.[1]) filename = match[1].replace(/['"]/g, "");
    }

    // Trigger download
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    handleRemoveFile();

    // SUCCESS: Even if file is returned, if status is 200 → success!
    if (response.ok || response.status === 200 || response.status === 201) {
      toast.current?.show({
        severity: "success",
        summary: "Upload Successful!",
        detail: "File downloaded successfully.",
        life: 8000,
        closable: true,
      });
    } else {
      // Real error file (e.g. validation failed)
      toast.current?.show({
        severity: "error",
        summary: "Upload Failed",
        detail: "Some records failed. Check the downloaded error file.",
        life: 0,
        sticky: true,
        closable: true,
      });
    }
  } 
  // CASE 2: Normal JSON success (no file)
  else if (response.ok) {
    const data = await response.json();
    toast.current?.show({
      severity: "success",
      summary: "Success",
      detail: data?.message || "File uploaded and processed successfully!",
      life: 6000,
      closable: true,
    });
    handleRemoveFile();
    setSelectedEntity("");
  } 
  // CASE 3: JSON error response
  else {
    let errorMessage = "Upload failed.";
    try {
      const errorData = await response.json();
      errorMessage = 
        errorData?.message || 
        errorData?.detail || 
        errorData?.error || 
        JSON.stringify(errorData);
    } catch (e) {
      errorMessage = `Server error: ${response.status}`;
    }

    handleRemoveFile();

    toast.current?.show({
      severity: "error",
      summary: "Upload Failed",
      detail: errorMessage,
      life: 0,
      sticky: true,
      closable: true,
    });
  }
} catch (err) {
  handleRemoveFile();
  toast.current?.show({
    severity: "error",
    summary: "Network Error",
    detail: "Failed to connect. Please try again.",
    life: 0,
    sticky: true,
    closable: true,
  });
} finally {
  setLoading(false);
}
    
  };

  const handleClear = () => {
    handleRemoveFile();
    if (roleId === 1) setSelectedEntity("");
  };

  return (
    <div className="relative">
      {/* Loader Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <ClimbingBoxLoader color="#d97925" size={20} />
        </div>
      )}
      <div
        className="flex flex-col items-center justify-center  bg-cover bg-center p-4 text-white"
        style={{ backgroundImage: "url('/background.jpg')" }}
      >
        <Toast ref={toast} />
        <div className="backdrop-blur-md bg-white/10 border border-white/20 rounded-2xl shadow-lg p-8 w-full max-w-md">
          <h2 className="text-2xl font-bold mb-6 text-center">
            Service Certificate
          </h2>

          {/* Entity Dropdown for Superadmin */}
          {roleId === 1 && (
            <div className="mb-6">
              <label htmlFor="entity" className="block mb-2 text-sm">
                
              </label>
              <select
                id="entity"
                value={selectedEntity}
                onChange={(e) => setSelectedEntity(e.target.value)}
                className="w-full px-4 py-2 rounded-md bg-white/10 border border-white/20 text-black focus:outline-none focus:ring-2 focus:ring-[#d97925]"
              >
                <option value="">-- Select Entity --</option>
                {entityList.map((entity, index) => (
                  <option key={index} value={entity}>
                    {entity}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Download Button */}
          <div className="flex flex-col items-center mb-6">
            <button
              onClick={handleDownload}
              className="text-[#d97925] hover:text-white bg-white hover:bg-[#d97925] border border-[#d97925] rounded-full p-3 transition-all"
            >
              <Download size={20} />
            </button>
            <span className="mt-2 text-sm">Download Template</span>
          </div>

          {/* Upload Section for Uploader */}
          {isUploader && (
            <>
              <div className="flex flex-col items-center mb-4">
                <label className="cursor-pointer">
                  <div className="text-[#d97925] hover:text-white bg-white hover:bg-[#d97925] border border-[#d97925] rounded-full p-3 transition-all flex items-center justify-center">
                    <Upload size={20} />
                    <input
                      type="file"
                      accept=".xlsx"
                      onChange={handleFileChange}
                      className="hidden"
                      ref={fileInputRef}
                    />
                  </div>
                </label>
                <span className="mt-2 text-sm">Upload Document (.xlsx)</span>
              </div>

              {/* Uploaded File Preview */}
              {uploadedFile && (
                <div className="flex justify-between items-center bg-white/10 border border-white/20 rounded-full px-4 py-2 mb-4 text-sm text-white">
                  <span className="truncate">{uploadedFile.name}</span>
                  <button
                    onClick={handleRemoveFile}
                    className="text-red-400 hover:text-red-600 ml-4 text-sm"
                  >
                    Remove
                  </button>
                </div>
              )}

              {/* Submit and Clear Buttons */}
              <div className="flex gap-4 justify-center mt-4">
                <button
                  onClick={handleSubmit}
                  className="bg-[#d97925] hover:bg-white hover:text-[#d97925] text-white px-4 py-2 rounded-full border border-[#d97925] transition-all"
                >
                  Submit
                </button>
                <button
                  onClick={handleClear}
                  className="bg-white hover:bg-[#d97925] hover:text-white text-[#d97925] px-4 py-2 rounded-full border border-[#d97925] transition-all"
                >
                  Clear
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceCertificate;